import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Layout, PageHeader } from "@/components/Layout";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RTooltip,
  LineChart, Line, CartesianGrid,
} from "recharts";
import { Boxes, Workflow, Gauge, Euro, AlertTriangle, Trophy, Loader2, TrendingUp, BrainCircuit, ListChecks, Building2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const scoreColor = (s) => (s >= 7 ? "#00A859" : s >= 4.5 ? "#F59E0B" : "#FF2A2A");

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-border p-6 h-full" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="data-label">{label}</span>
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <div className="mt-4 font-mono font-medium text-3xl tracking-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);
  const [period, setPeriod] = useState("90");
  const [category, setCategory] = useState("all");
  const navigate = useNavigate();
  const { activeClientId, activeClient } = useClient();

  useEffect(() => {
    setStats(null);
    const params = activeClientId ? { client_id: activeClientId } : {};
    api.get("/dashboard/stats", { params }).then(({ data }) => setStats(data)).catch(() => {});
  }, [activeClientId]);

  useEffect(() => {
    const params = {};
    if (period !== "all") params.days = period;
    if (category !== "all") params.category = category;
    if (activeClientId) params.client_id = activeClientId;
    api.get("/dashboard/trend", { params }).then(({ data }) => setTrend(data)).catch(() => {});
  }, [period, category, activeClientId]);

  if (!stats) {
    return (
      <Layout>
        <PageHeader title="Dashboard" subtitle="Panoramica assessment" />
        <div className="flex items-center justify-center h-96 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </Layout>
    );
  }

  const scoped = !!activeClientId;

  return (
    <Layout>
      <PageHeader title="Dashboard" subtitle={scoped ? `${activeClient?.name || "Cliente"} · assessment IT` : "Portfolio · tutti i clienti"} />
      <div className="p-4 sm:p-8 space-y-4">
        {scoped && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="client-readiness-kpis">
            <Kpi icon={BrainCircuit} label="Readiness" value={stats.readiness_overall != null ? `${stats.readiness_overall}` : "—"} sub={stats.readiness_tier?.label || "nessun assessment"} testid="kpi-readiness" />
            <Kpi icon={Gauge} label="Assessment" value={stats.assessments_count ?? 0} sub="storico cliente" testid="kpi-assessments" />
            <Kpi icon={TrendingUp} label="KPI monitorati" value={stats.kpi_count ?? 0} testid="kpi-kpicount" />
            <Kpi icon={ListChecks} label="Task in ritardo" value={stats.overdue_tasks ?? 0} sub={`${stats.open_tasks ?? 0} task aperti`} testid="kpi-overdue" />
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Kpi icon={Boxes} label="Tool valutati" value={stats.total_tools} testid="kpi-tools" />
          <Kpi icon={Gauge} label="Punteggio medio" value={stats.avg_score} sub="su scala 0–10" testid="kpi-avg" />
          <Kpi icon={Euro} label="Costo mensile" value={`€ ${stats.total_monthly_cost}`} sub="totale stack" testid="kpi-cost" />
          <Kpi icon={Workflow} label="Processi" value={stats.total_processes} testid="kpi-processes" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-border p-6" data-testid="ranking-chart">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading font-bold text-lg tracking-tight">Classifica Tool</h3>
                <div className="data-label mt-1">Punteggio ponderato</div>
              </div>
              {stats.top_tool && (
                <div className="flex items-center gap-2 text-sm">
                  <Trophy className="w-4 h-4 text-primary" />
                  <span className="font-medium">{stats.top_tool}</span>
                </div>
              )}
            </div>
            {stats.ranking.length === 0 ? (
              <div className="text-sm text-muted-foreground py-12 text-center">
                Nessun tool valutato. <button className="text-primary underline" onClick={() => navigate("/tools")}>Aggiungi il primo tool</button>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={stats.ranking} layout="vertical" margin={{ left: 20, right: 30 }}>
                  <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} stroke="#4B5563" />
                  <RTooltip cursor={{ fill: "#F3F4F6" }} contentStyle={{ borderRadius: 0, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Bar dataKey="score" radius={0}>
                    {stats.ranking.map((r, i) => (
                      <Cell key={i} fill={scoreColor(r.score)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-white border border-border p-6" data-testid="criticality-card">
              <div className="flex items-center justify-between">
                <span className="data-label">Processi ad alta criticità</span>
                <AlertTriangle className="w-4 h-4 text-destructive" />
              </div>
              <div className="mt-4 font-mono font-medium text-3xl">{stats.high_criticality_count}</div>
              <button onClick={() => navigate("/processes")} className="mt-4 text-sm text-primary hover:underline">
                Analizza processi →
              </button>
            </div>
            <div className="bg-white border border-border p-6">
              <span className="data-label">Criteri di valutazione</span>
              <div className="mt-4 font-mono font-medium text-3xl">{stats.criteria_count}</div>
              <button onClick={() => navigate("/criteria")} className="mt-4 text-sm text-primary hover:underline">
                Gestisci criteri →
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border p-6" data-testid="stack-trend-chart">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              <div>
                <h3 className="font-heading font-bold text-lg tracking-tight">Andamento dello stack</h3>
                <div className="data-label mt-1">{category === "all" ? "Punteggio medio nel tempo" : `Categoria: ${category}`}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-none h-9 w-44" data-testid="trend-category-select"><SelectValue placeholder="Categoria" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {(stats.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="rounded-none h-9 w-36" data-testid="trend-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                  <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                  <SelectItem value="all">Tutto lo storico</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {trend.length >= 2 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trend.map((t) => ({ data: new Date(t.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }), punteggio: t.avg_score, tool: t.tools }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="data" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                <YAxis domain={[0, 10]} tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" />
                <RTooltip contentStyle={{ borderRadius: 0, border: "1px solid #E5E7EB", fontSize: 12 }} formatter={(v, n) => n === "punteggio" ? [v, "Media"] : [v, "Tool"]} />
                <Line type="monotone" dataKey="punteggio" stroke="#002FA7" strokeWidth={2} dot={{ r: 3, fill: "#002FA7" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">
              {trend.length === 0 && (period !== "all" || category !== "all")
                ? "Nessuna rilevazione nel periodo/categoria selezionati."
                : "Il trend si popola automaticamente man mano che i punteggi dei tool cambiano nel tempo. Servono almeno 2 rilevazioni."}
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
}
